import { useId, type ReactNode } from 'react';
import type { History, Platform } from './types';
import { wavePaths } from './quality';
import { Button as SxButton, Chip, Icon, Segmented as SxSegmented, Toggle } from '../ui';

/* ── color maps (literal class names so Tailwind keeps them) ───────────────── */
export type StateColor = 'live' | 'start' | 'warn' | 'fail' | 'idle';
export const TXT: Record<StateColor, string> = { live: 'text-live', start: 'text-start', warn: 'text-warn', fail: 'text-fail', idle: 'text-idle' };
export const BORDER: Record<StateColor, string> = { live: 'border-live', start: 'border-start', warn: 'border-warn', fail: 'border-fail', idle: 'border-idle' };
export const BORDER50: Record<StateColor, string> = { live: 'border-live/50', start: 'border-start/50', warn: 'border-warn/50', fail: 'border-fail/50', idle: 'border-idle/50' };
export const BORDER45: Record<StateColor, string> = { live: 'border-live/[.45]', start: 'border-start/[.45]', warn: 'border-warn/[.45]', fail: 'border-fail/[.45]', idle: 'border-idle/30' };
export const BG12: Record<StateColor, string> = { live: 'bg-live/[.12]', start: 'bg-start/[.12]', warn: 'bg-warn/[.12]', fail: 'bg-fail/[.12]', idle: 'bg-idle/[.12]' };
export const STROKE: Record<StateColor, string> = { live: 'stroke-live', start: 'stroke-start', warn: 'stroke-warn', fail: 'stroke-fail', idle: 'stroke-idle' };
export const FILL: Record<StateColor, string> = { live: 'fill-live', start: 'fill-start', warn: 'fill-warn', fail: 'fill-fail', idle: 'fill-idle' };

/*
 * A leading state glyph in a text ("▲ SLOW LINK", "● SENDING") is dropped where the component now
 * draws the system's state icon: the icon carries it (06 «no Unicode symbols as icons»). The texts
 * themselves lose their glyphs in the surfaces' PRs (AUDIT F-08).
 */
const GLYPH = /^[●○▲▼✕◔■↻✓‖★⌖─✗◐]\s*/;
export const plain = (c: ReactNode): ReactNode => (typeof c === 'string' ? c.replace(GLYPH, '') : c);

/* ── platform logo slot ─────────────────────────────────────────────────────
 * 30×30 slot. Engineering drops in the platform's official one-color SVG (fill: currentColor)
 * from its brand kit. Until then: monogram. Custom RTMP uses our own signal icon.
 */
export const MONO: Record<Platform, string> = { youtube: 'YT', twitch: 'TW', kick: 'KK', facebook: 'FB', instagram: 'IG', tiktok: 'TT', rtmp: 'RT' };
export const PLATFORM_LABEL: Record<Platform, string> = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', rtmp: 'RTMP' };

export function RtmpIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true" className="block">
      <circle cx="9" cy="12.5" r="1.8" fill="currentColor" />
      <path d="M5.6 9.2a4.8 4.8 0 0 1 6.8 0M3.2 6.6a8.2 8.2 0 0 1 11.6 0M9 14.3V16.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Official marks the tile shows (web/public/platforms, owners' files unaltered; scale evens out their own clear space).
 * `light`: an owner who supplies one icon per ground (TikTok: black ground on dark, white ground on light) —
 * the system's `img.dk` + `img.lt` pair (PlatformMarks, Code).
 */
export const MARK: Partial<Record<Platform, { src: string; light?: string; scale: number }>> = {
  youtube: { src: '/platforms/youtube.svg', scale: 1.45 },
  twitch: { src: '/platforms/twitch.svg', scale: 0.88 },
  // DS: VT-22 — the snapshot's PNGs unaltered (vector pack pending); clear space at 30 / 18 px not checked yet
  tiktok: { src: '/platforms/tiktok-on-dark.png', light: '/platforms/tiktok-on-light.png', scale: 1 },
  // DS: VT-09 — Instagram glyph (Meta's file, unaltered) until the system's PlatformMarks carries it; in every tile size,
  // also under Meta's 29 px minimum (owner 02.11, VT-24: he settles it with Meta)
  instagram: { src: '/platforms/instagram.svg', scale: 0.9 },
};

/** The images of a mark inside `.sx-tile.mark`: one, or the dark / light pair. */
export function MarkImg({ mark }: { mark: { src: string; light?: string; scale: number } }) {
  const style = mark.scale !== 1 ? { transform: `scale(${mark.scale})` } : undefined;
  if (!mark.light) return <img src={mark.src} alt="" style={style} draggable={false} />;
  return (
    <>
      <img className="dk" src={mark.src} alt="" style={style} draggable={false} />
      <img className="lt" src={mark.light} alt="" style={style} draggable={false} />
    </>
  );
}

/** The system's platform tile (.sx-tile): the owner's mark when we have it, else the monogram (OutputCard, CB-17). */
export function LogoSlot({ platform, dim, dashed }: { platform: Platform | null; dim?: boolean; dashed?: boolean }) {
  const mark = platform ? MARK[platform] : undefined;
  return (
    <span
      title={platform ? PLATFORM_LABEL[platform] : 'New destination'}
      className={`sx-tile ${mark ? 'mark' : ''} ${dashed || !platform ? 'pending' : ''} ${dim ? 'opacity-[.45]' : ''}`}
    >
      {mark ? <MarkImg mark={mark} />
        : platform === 'rtmp' ? <RtmpIcon /> : platform ? MONO[platform] : '+'}
    </span>
  );
}

/*
 * ── sparkline: the bitrate of the last 5 minutes (03-data-viz «Sparklines»: no axes, 1.5 px line, the
 * value printed beside it). The line *means* a state, so it wears it: live while healthy, warn while
 * something is off (03 «When a line means a state…», OutputCard «live (sparkline in live)»). `idle`:
 * the windows without a stream as a dashed slot line (Status table); the output row leaves them out.
 */
export function Spark({ h, tone, w = 90, stroke = 1.4, idle = true }: { h: History; tone: StateColor; w?: number; stroke?: number; idle?: boolean }) {
  const p = wavePaths(h, w, 18);
  return (
    <svg width={w} height="18" viewBox={`0 0 ${w} 18`} aria-hidden="true" className="spark flex-none" data-testid="spark">
      {idle && <path d={p.idle} className="stroke-slot" strokeDasharray="2 3" fill="none" />}
      <path d={p.good + p.warn + p.bad} className={STROKE[tone]} strokeWidth={stroke} strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/* ── status badge: the system's chip — border style, icon and word per state (StateSystem) ── */
export function StatusBadge({ color, children }: { color: StateColor; border?: 'solid' | 'dashed' | 'dotted'; children: ReactNode }) {
  return <Chip state={color} className="flex-none">{plain(children)}</Chip>;
}

/* ── relay toggle ─────────────────────────────────────────────────────────────
 * The system's switch: on / off only, the output's state is in its row (OutputCard, AUDIT CB-05).
 * Never flips instantly: while the node confirms it shows "pending" (start colour, knob in the
 * middle) and is disabled. On a phone the hit area grows to 44 px around it.
 */
export function RelayToggle({ on, busy, label, onToggle, size = 'md', testId }: {
  on: boolean; busy?: boolean; label: string; onToggle?: () => void; size?: 'md' | 'touch' | 'sm'; testId?: string;
}) {
  // 44 px to touch on a phone: a transparent 12 px border around the 36 × 20 switch (the knob and
  // the inset edge sit inside the border, the background stops at it), so the switch looks the same.
  const hit =
    size === 'sm' ? ''
    : size === 'touch' ? 'h-[44px] w-[60px] border-[12px] border-transparent bg-clip-padding'
    : 'max-md:h-[44px] max-md:w-[60px] max-md:border-[12px] max-md:border-transparent max-md:bg-clip-padding';
  return (
    <Toggle
      checked={on}
      pending={busy}
      size={size === 'sm' ? 'sm' : undefined}
      aria-label={label}
      disabled={busy}
      onChange={() => onToggle?.()}
      data-testid={testId}
      className={hit}
    />
  );
}

/* ── edit button: the system's icon button (tune / close); open = accent, needs you = warn ── */
export function EditButton({ editing, attention, label, onClick, size = 32 }: { editing: boolean; attention?: boolean; label: string; onClick?: () => void; size?: 32 | 44 }) {
  const hl = attention && !editing;
  const look = editing ? 'border-accent bg-accent/10 text-accent' : hl ? 'border-warn border-dashed bg-warn/10 text-warn' : '';
  return (
    <SxButton
      icon
      aria-label={(editing ? 'Close settings for ' : 'Edit ') + label + (hl ? ' — needs your attention' : '')}
      title={(editing ? 'Close settings for ' : 'Edit ') + label}
      aria-expanded={editing}
      onClick={onClick}
      className={`flex-none ${look} ${size === 44 ? 'h-11 w-11' : 'max-md:h-11 max-md:w-11 md:h-8 md:w-8'}`}
    >
      <Icon name={editing ? 'close' : 'tune'} size={16} />
    </SxButton>
  );
}

/* ── buttons: the system's (Buttons) — one primary per view ── */
export function PrimaryButton({ children, disabled, busy, onClick, className = '', type = 'button', testId }: { children: ReactNode; disabled?: boolean; busy?: boolean; onClick?: () => void; className?: string; type?: 'button' | 'submit'; testId?: string }) {
  return (
    <SxButton variant="primary" type={type} data-testid={testId} disabled={disabled || busy} busy={busy} onClick={onClick} className={className}>
      {children}
    </SxButton>
  );
}

export function SecondaryButton({ children, disabled, onClick, className = '' }: { children: ReactNode; disabled?: boolean; onClick?: () => void; className?: string }) {
  return (
    <SxButton disabled={disabled} onClick={onClick} className={className}>
      {children}
    </SxButton>
  );
}

export function DangerLink({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <SxButton variant="ghost" size="sm" disabled={disabled} onClick={onClick} className="self-start px-0 text-fail">
      {children}
    </SxButton>
  );
}

/* ── banners: a state icon and the text, the frame in the state's colour ── */
const TONE = { info: 'idle', warn: 'warn', err: 'fail', ok: 'live' } as const;
export function Banner({ tone, children }: { tone: 'info' | 'warn' | 'err' | 'ok'; children: ReactNode }) {
  const look = {
    info: 'border-line-2 text-text-soft',
    warn: 'border-dashed border-warn/45 bg-warn/10 text-warn',
    err: 'border-double border-[3px] border-fail/45 bg-fail/10 text-fail',
    ok: 'border-live/45 text-live',
  }[tone];
  return (
    <div role={tone === 'err' ? 'alert' : 'status'} className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs ${look}`}>
      <Icon name={`state-${TONE[tone]}`} size={12} className="mt-px" />
      <span className="min-w-0">{plain(children)}</span>
    </div>
  );
}

/* ── form pieces: the system's field (Fields) — line-strong edge, accent + ring on focus ── */
export function Field({ label, value, placeholder, mono, type = 'text', error, warn, hint, focus, disabled, action, onChange, name, autoComplete }: {
  label: string; value?: string; placeholder?: string; mono?: boolean; type?: 'text' | 'password';
  error?: string; warn?: string; hint?: string; focus?: boolean; disabled?: boolean;
  action?: { label: string; onClick?: () => void }; onChange?: (v: string) => void; name?: string; autoComplete?: string;
}) {
  const id = useId();
  const ring = error ? '' : warn ? 'border-warn' : focus ? 'border-accent shadow-focus' : 'focus-within:border-accent focus-within:shadow-focus';
  return (
    <div className="sx-field" data-state={error ? 'fail' : undefined}>
      <label htmlFor={id} className={disabled ? 'opacity-50' : undefined}>{label}</label>
      <span className={`sx-input gap-1.5 pr-1 ${mono ? '' : 'sans'} ${ring}`} aria-disabled={disabled || undefined}>
        <input
          id={id}
          type={type}
          name={name}
          autoComplete={autoComplete}
          value={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={!!error}
          onChange={(e) => onChange?.(e.target.value)}
          className="h-full min-w-0 flex-1 bg-transparent text-inherit outline-none placeholder:text-muted"
        />
        {action && (
          <SxButton size="sm" onClick={action.onClick} className="h-[28px] font-sans">
            {action.label}
          </SxButton>
        )}
      </span>
      {error ? (
        <span className="err"><Icon name="state-fail" size={12} />{error}</span>
      ) : warn ? (
        <span className="err text-warn"><Icon name="state-warn" size={12} />{warn}</span>
      ) : hint ? (
        <span className="help">{hint}</span>
      ) : null}
    </div>
  );
}

/** Segmented control of a form: the system's segment, selection in accent with its edge (CB-08, WZ-02). */
export function Segmented<T extends string>({ label, options, value, disabled = [], onChange }: {
  label?: string; options: { value: T; label: string }[]; value: T; disabled?: T[]; onChange?: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-[13px] text-text-soft">{label}</span>}
      <SxSegmented block label={label} options={options} value={value} disabled={disabled} onChange={(v) => onChange?.(v)} />
    </div>
  );
}

export function Switch({ label, on, onChange, sub }: { label: string; on: boolean; onChange?: (v: boolean) => void; sub?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-[13px] text-text-soft">{label}</span>
        <Toggle checked={on} aria-label={label} onChange={(v) => onChange?.(v)} />
      </div>
      {sub && <span className="text-[11px] text-muted">{sub}</span>}
    </div>
  );
}

export function SelectRow({ label, value, error, warn, sub, subTone = 'mute' }: {
  label: string; value: string; error?: boolean; warn?: boolean; sub?: string; subTone?: 'ok' | 'start' | 'warn' | 'err' | 'mute';
}) {
  const subC = { ok: 'text-live', start: 'text-start', warn: 'text-warn', err: 'text-fail', mute: 'text-muted' }[subTone];
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-[13px] text-text-soft">{label}</span>
        {/* Replace with a real <select>/listbox; styled trigger shown here. */}
        <button type="button" aria-haspopup="listbox"
          className={`sx-select min-w-[150px] font-mono text-xs text-text ${error ? 'border-fail shadow-error' : warn ? 'border-warn border-dashed' : ''}`}>
          {value}<Icon name="chevron-down" size={16} className="text-muted" />
        </button>
      </div>
      {sub && <span className={`text-[11px] ${subC}`}>{sub}</span>}
    </div>
  );
}

/** Inline confirmation inside the card (owner 23.10: confirmations stay inline, no modal over a live stream). */
export function InlineConfirm({ tone, title, body, no, yes, onNo, onYes }: {
  tone: 'bad' | 'warn'; title: string; body: string; no: string; yes: string; onNo?: () => void; onYes?: () => void;
}) {
  const c = tone === 'bad' ? 'border-fail/45 bg-fail/[.07] text-fail' : 'border-dashed border-warn/45 bg-warn/[.07] text-warn';
  return (
    <div role="alertdialog" aria-label={title} className={`flex flex-col gap-2.5 rounded-md border p-3 ${c}`}>
      <div className="flex items-center gap-2 font-medium">
        <Icon name={tone === 'bad' ? 'state-fail' : 'state-warn'} size={16} />
        {title}
      </div>
      <div className="text-[13px] text-text-soft">{body}</div>
      <div className="grid grid-cols-2 gap-2">
        {/* touch targets ≥ 44 px on a phone, like the rest of the panel */}
        <SxButton variant="ghost" className="max-md:min-h-[44px]" onClick={onNo}>{no}</SxButton>
        <SxButton variant={tone === 'bad' ? 'danger' : 'default'} className={`max-md:min-h-[44px] ${tone === 'warn' ? 'border-warn text-warn' : ''}`} onClick={onYes}>{yes}</SxButton>
      </div>
    </div>
  );
}
